/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMask } from '@fortawesome/free-solid-svg-icons';

import Icon from 'app/components/commons/Icon';
import t from 'app/utils/translate';
import FeaturedIcon from '../icons/FeaturedIcon';
import PlayerSelectionIcon from '../icons/PlayerSelectionIcon';
import Tooltip from '../commons/Tooltip';
import CasualContestIcon from '../icons/CasualContestIcon';
import RookieIcon from 'app/images/app/Rookie.svg';
import RestrictedContestIcon from '../icons/RestrictedContestIcon';

type Props = {
  captain?: boolean,
  drop1?: boolean,
  featured?: boolean,
  isRestricted?: boolean,
  isPrivate?: boolean,
  isCasualContest?: boolean,
  ownerType?: 'admin_users' | 'users',
  showPlayerSelections?: boolean,
  rookieOnly?: boolean
};

const defaultProps = {
  captain: false,
  drop1: false,
  featured: false,
  isRestricted: false,
  isPrivate: false,
  isCasualContest: false,
  ownerType: undefined,
  showPlayerSelections: false,
  rookieOnly: false
};

const CaptainIconWrapper = styled.span(({ theme }) => ({
  color: theme.color.blue,
  paddingTop: 7,
  paddingLeft: '0.25em'
}));

const IconWrapper = styled.span({
  padding: '0 0.125em'
});

const LegendIcon = styled(IconWrapper)({
  fontSize: '0.8em',
  width: '0.8em'
});

const Wrapper = styled.span({});

const UserIcon = styled(Icon)({
  verticalAlign: 'sub',
  fill: '#fd7024'
});

const ChallengeIcons: React.FC<Props> = ({
  captain,
  drop1,
  featured,
  isPrivate,
  isRestricted,
  ownerType,
  isCasualContest,
  showPlayerSelections,
  rookieOnly
}) => (
  <Wrapper>
    {showPlayerSelections && (
      <IconWrapper>
        <Tooltip title="Player selection % displayed">
          <PlayerSelectionIcon />
        </Tooltip>
      </IconWrapper>
    )}
    {featured && (
      <IconWrapper>
        <Tooltip title="Featured contest">
          <FeaturedIcon />
        </Tooltip>
      </IconWrapper>
    )}
    {captain && (
      <CaptainIconWrapper>
        <Tooltip title="Captain contest">
          <span>
            <Icon type="icon-captain" />
          </span>
        </Tooltip>
      </CaptainIconWrapper>
    )}
    {drop1 && (
      <IconWrapper>
        <Tooltip title="Drop1 contest format">
          <span>
            <Icon type="icon-drop1" />
          </span>
        </Tooltip>
      </IconWrapper>
    )}
    {ownerType && ownerType === 'users' && (
      <IconWrapper>
        <Tooltip title={`User created ${t('challenge').toLowerCase()}`}>
          <span>
            <UserIcon type="icon-user" size="1.2em" />
          </span>
        </Tooltip>
      </IconWrapper>
    )}
    {isPrivate && (
      <IconWrapper>
        <Tooltip title={`Private ${t('challenge').toLowerCase()}`}>
          <FontAwesomeIcon icon={faMask} color="#fd7024" size="sm" />
        </Tooltip>
      </IconWrapper>
    )}
    {isRestricted && (
      <LegendIcon>
        <Tooltip title={`You are ineligible to enter this ${t('challenge').toLowerCase()}`}>
          <RestrictedContestIcon />
        </Tooltip>
      </LegendIcon>
    )}
    {isCasualContest && !isRestricted && (
      <LegendIcon>
        <Tooltip title={`Casual ${t('challenge').toLowerCase()}`}>
          <CasualContestIcon isRestricted={isRestricted} />
        </Tooltip>
      </LegendIcon>
    )}
    {rookieOnly && (
      <IconWrapper>
        <RookieImg src={RookieIcon} alt="rookies" width={13} />
      </IconWrapper>
    )}
  </Wrapper>
);
const RookieImg = styled.img({
  display: 'inline-block',
  paddingBottom: 2
});
ChallengeIcons.defaultProps = defaultProps;

export default React.memo(ChallengeIcons);
